import react, { useState, useRef, useEffect } from "react";

export const useHorizontalScroll = () => {
  const [showArrows, setShowArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const containerRef = useRef<any>(null);
  const scrollIntervalRef = useRef<any>(null);

  const checkScroll = () => {
    const container = containerRef.current;
    if (container) {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      setShowArrows(hasOverflow);
      // 只要有滚动量就可以向左滚动
      setCanScrollLeft(currentScroll > 0);
      // 只要没到最右边就可以向右滚动
      setCanScrollRight(currentScroll < maxScroll);
    }
  };

  // 确保在组件挂载和图片加载后都检查滚动状态
  useEffect(() => {
    const timer = setTimeout(checkScroll, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    checkScroll();
  }, [imagesLoaded]);

  const handleImageLoad = () => {
    setImagesLoaded((prev) => prev + 1);
  };

  const scroll = (direction: string) => {
    const container = containerRef.current;
    if (container) {
      const scrollAmount = 50;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });

      // 滚动后立即检查状态
      setTimeout(checkScroll, 50);
    }
  };

  const startScrolling = (direction: string) => {
    if (scrollIntervalRef.current) return;

    // 先执行一次滚动
    scroll(direction);

    // 设置连续滚动
    scrollIntervalRef.current = setInterval(() => {
      const container = containerRef.current;
      if (!container) return;

      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (direction === "left" && currentScroll <= 0) {
        stopScrolling();
      } else if (direction === "right" && currentScroll >= maxScroll) {
        stopScrolling();
      } else {
        scroll(direction);
      }
    }, 100);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    // 停止滚动后检查状态
    checkScroll();
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      checkScroll();
    }

    return () => {
      resizeObserver.disconnect();
      stopScrolling();
    };
  }, []);

  return {
    containerRef,
    showArrows,
    canScrollLeft,
    canScrollRight,
    checkScroll,
    startScrolling,
    stopScrolling,
    handleImageLoad,
  };
};
